import {SessionLogs} from '../session-logs/SessionLogs'
import {EventSessionLogs} from '../session-logs/EventSessionLogs'
import {SessionHeader} from './SessionHeader'
import {SessionFooter} from './SessionFooter'
import {SessionFailedBanner} from '../session-logs/SessionFailedBanner'
import {SessionCancelledBanner} from '../session-logs/SessionCancelledBanner'

import {useEffect, useRef, useState} from 'react'
import {useSessionContext} from '../../contexts/SessionContext'
import type {Pull} from '../../types/pull'
import {isActiveSession, SessionState, isPendingSession} from '../../types/session'
import {useAgentProfile} from '../../contexts/AgentProfileContext'
import {SessionLogsProvider} from '../session-logs/Context'
import {useSelectedModel} from '../../hooks/use-selected-model'
import {generateWorkflowRunLink} from '../../utils/generate-actions-link'
import styles from './Session.module.css'
import {useIntentDetection} from '../../hooks/useIntentDetection'
import {WithShimmerEffect} from '@github-ui/copilot-chat/components/WithShimmerEffect'
import {Stack} from '@primer/react'
import {AgentAvatar} from '../AgentAvatar'
import {CopilotAnimationType} from '@github-ui/copilot-animation'
import {isFeatureEnabled} from '@github-ui/feature-flags'

const GLOBAL_TOGGLE_EVENT = 'session-global-toggle'

interface SessionProps {
  defaultOpen?: boolean
  onOpenChange: (open: boolean) => void
  onToolExpand?: (expand: boolean) => void
  shouldHighlight?: boolean
  pull?: Pull
  logsPollingInterval?: number
  nwo?: string
}

export function Session({
  defaultOpen = false,
  onOpenChange,
  onToolExpand,
  shouldHighlight = false,
  pull,
  logsPollingInterval,
  nwo,
}: SessionProps) {
  const {session} = useSessionContext()
  const selectedModel = useSelectedModel(session.model)

  // Use provided nwo, fall back to pull's repository_nwo for backwards compatibility
  const effectiveNwo = nwo ?? pull?.repository_nwo

  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [logsAreLoading, setLogsAreLoading] = useState(false)
  const sessionContainerRef = useRef<HTMLDivElement>(null)
  const toolInstanceRef = useRef({})

  const {isChatSession, isUsingIntentDetection} = useIntentDetection(session)
  const {singleSessionAgent} = useAgentProfile()

  const handlerRef = useRef<(event: Event) => void>((event: Event) => {
    const customEvent = event as CustomEvent<{expand: boolean; excludeInstance: object}>
    if (customEvent.detail.excludeInstance !== toolInstanceRef.current) {
      const shouldExpand = customEvent.detail.expand
      setIsOpen(shouldExpand)
    }
  })

  // Set up global expand/collapse event listener
  useEffect(() => {
    const globalToggleHandler = (event: Event) => {
      handlerRef.current?.(event)
    }

    document.addEventListener(GLOBAL_TOGGLE_EVENT, globalToggleHandler)

    return () => {
      document.removeEventListener(GLOBAL_TOGGLE_EVENT, globalToggleHandler)
    }
  }, [])

  const handleModifierClick = (expand: boolean) => {
    setIsOpen(expand)

    // Dispatch event to all other tools
    const event = new CustomEvent(GLOBAL_TOGGLE_EVENT, {
      detail: {expand, excludeInstance: toolInstanceRef.current},
    })
    document.dispatchEvent(event)
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    onOpenChange(open)
  }

  const isActive = isActiveSession(session)
  const isPending = isPendingSession(session)
  const contentId = `session-content-${session.id}`
  const shouldShowSessionFooter =
    (session.state === SessionState.Completed || session.state === SessionState.InProgress) &&
    session.premium_requests > 0 &&
    !logsAreLoading

  // Scroll to this session if it should be highlighted.
  useEffect(() => {
    if (!shouldHighlight) return
    if (!sessionContainerRef.current) return

    // Use back-to-back `requestAnimationFrame`s to wait for layout to settle before scrolling.
    let frameId = 0
    let secondFrameId = 0
    frameId = requestAnimationFrame(() => {
      secondFrameId = requestAnimationFrame(() => {
        sessionContainerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      })
    })

    return () => {
      cancelAnimationFrame(frameId)
      cancelAnimationFrame(secondFrameId)
    }
  }, [shouldHighlight])

  // For intent detection POC we don't want to show things like the session box early.
  // Use a more classic chat display, then it will shift to session or chat as needed.
  if (isPending && isUsingIntentDetection) {
    return (
      <Stack direction="horizontal" padding="none" align="center" justify="start" gap="condensed">
        <AgentAvatar animationType={CopilotAnimationType.Thinking} loopAnimation />
        <WithShimmerEffect className="color-fg-muted">Planning response...</WithShimmerEffect>
      </Stack>
    )
  }

  const containerClassNames = isChatSession
    ? `${styles.contentContainer} ${styles.chatContentContainer}`
    : styles.contentContainer

  return (
    <div ref={sessionContainerRef} data-active={isActive} data-session-id={session.id}>
      {!singleSessionAgent && (
        <SessionHeader
          nwo={effectiveNwo}
          isCollapsed={!isOpen}
          onModifierClick={handleModifierClick}
          contentId={contentId}
          onOpenChange={handleOpenChange}
          shouldHighlight={shouldHighlight}
        />
      )}
      <div id={contentId} className={containerClassNames} data-expanded={isOpen || isChatSession || singleSessionAgent}>
        <div className={styles.logsContainer}>
          <SessionLogsProvider
            variant="mission-control"
            onToolExpand={onToolExpand}
            session={session}
            sessionNwo={effectiveNwo}
          >
            {isFeatureEnabled('copilot_mission_control_session_events_ui') ? (
              <EventSessionLogs taskId={session.task_id} pollingInterval={logsPollingInterval ?? 3000} />
            ) : (
              <SessionLogs
                logsPollingInterval={logsPollingInterval ?? 3000}
                nwo={effectiveNwo}
                onLogsLoadingChange={setLogsAreLoading}
              />
            )}
          </SessionLogsProvider>
        </div>
        {shouldShowSessionFooter && (
          <SessionFooter
            showFeedbackButtons={!isActiveSession(session)}
            numberOfPremiumRequests={session.premium_requests}
            model={selectedModel?.displayName}
          />
        )}
      </div>
      <div className="tmp-mt-3">
        <SessionErrorBanner nwo={effectiveNwo} />
      </div>
    </div>
  )
}

export function SessionErrorBanner({nwo}: {nwo?: string}) {
  const {session} = useSessionContext()

  if (!session.error?.message) return null

  const workflowLink = session.workflow_run_id
    ? (generateWorkflowRunLink(nwo ?? null, session.workflow_run_id) ?? null)
    : null

  if (session.state === SessionState.Cancelled) {
    return <SessionCancelledBanner cancellationMessage={session.error.message} workflowRunLink={workflowLink} />
  }

  return <SessionFailedBanner errorMessage={session.error.message} workflowRunLink={workflowLink} />
}
